import { Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { BabylonGlobalItem } from '@ctt-babylon/lib/interfaces/babylon-global-item.interface';
import {
    BabylonBodyExtra,
    BabylonBreadcrumbI,
    BabylonEngineI,
    BabylonFooterContactI,
    BabylonFooterLinksI,
    BabylonFooterLogosI,
    BabylonHeaderBookShowI,
    BabylonHeaderClearI,
    BabylonHeaderMenuCenterI,
    BabylonHeaderMenuLogoCenterI,
    BabylonHeaderMenuShowI,
    BabylonModalService,
    BabylonOffersListI,
    BabylonStaticFooterI,
    BabylonStaticFooterV2I,
    BabylonSubmenuI,
    BabylonTopSliderI,
    BabylonTopSliderThumbsI,
    BabylonTopSliderVideoI,
    BodyComponent,
    COMPONENTS_EXTRA,
    PageConfig,
    PageConfigHead,
    Utils,
} from 'ctt-babylon';

export type BabylonHeadersType =
    | 'headerBookShow'
    | 'headerClear'
    | 'headerMenuCenter'
    | 'headerMenuShow'
    | 'headerCenterBookShow'
    | 'headerClearEngineShow'
    | 'headerMenuShowEngineShow'
    | 'C4MeTxtSvgCtaComponent'
    | 'HeSvgListComponent'
    | 'BabylonHeMeSvgTextCtaMot'
    | 'headerClearV2'
    | 'ModuloMenuHomeComponent';

@Injectable({
    providedIn: 'root',
})
export class LayoutService {
    private readonly headerData = signal<
        | BabylonHeaderBookShowI
        | BabylonHeaderClearI
        | BabylonHeaderMenuCenterI
        | BabylonHeaderMenuShowI
        | undefined
        // BabylonHeaderBookShowI | undefined
    >(undefined);

    readonly footer = signal<
        BodyComponent<BabylonStaticFooterI | BabylonStaticFooterV2I> | undefined
    >(undefined);

    readonly foC0SvgLink = signal<
        BodyComponent<BabylonStaticFooterI | BabylonStaticFooterV2I> | undefined
    >(undefined);

    private readonly engineData = signal<BabylonEngineI | undefined>(undefined);

    private readonly pageHead = signal<PageConfigHead | undefined>(undefined);

    readonly dynamicHeader = signal<boolean>(false);

    readonly pageDataLoaded = signal<boolean>(false);

    private readonly loadFooter = signal<boolean>(false);

    private readonly loadHeaders = signal<boolean>(false);

    private readonly advantagesUp = signal<boolean>(false);

    private readonly pageType = signal<boolean>(false);

    private readonly _pageType = signal<string | null>(null);

    private readonly selectedHeader = signal<BabylonHeadersType>('headerClear');

    readonly breadcrumbs = signal<BabylonBreadcrumbI | undefined>(undefined);

    readonly breadcrumbsv2 = signal<BabylonBreadcrumbI | undefined>(undefined);

    private readonly globalModal = signal<any[] | undefined>(undefined);

    private readonly modalPopup = signal<any[] | undefined>(undefined);

    readonly offersList = signal<BodyComponent<BabylonOffersListI> | undefined>(
        undefined
    );

    readonly roomsModal = signal<
        BodyComponent<BabylonGlobalItem> | any | undefined
    >(undefined);

    readonly topSlider = signal<
        | BodyComponent<
              | BabylonTopSliderI
              | BabylonTopSliderVideoI
              | BabylonTopSliderThumbsI
          >
        | undefined
    >(undefined);

    readonly footerLogos = signal<
        BodyComponent<BabylonFooterLogosI> | undefined
    >(undefined);
    readonly footerLinks = signal<
        BodyComponent<BabylonFooterLinksI> | undefined
    >(undefined);
    readonly footerLinksSvg = signal<any | undefined>(undefined);
    readonly footerContact = signal<
        BodyComponent<BabylonFooterContactI> | undefined
    >(undefined);

    readonly folislinks = signal<
        BodyComponent<BabylonFooterLinksI> | undefined
    >(undefined);

    readonly foC3TxtIcoV2 = signal<
        BodyComponent<BabylonFooterLinksI> | undefined
    >(undefined);

    readonly foc3imgtxt = signal<BodyComponent<any> | undefined>(undefined);

    readonly pageType$ = toObservable(this._pageType);

    private readonly engineAlreadyExists = signal<boolean>(false);

    readonly submenu = signal<BabylonSubmenuI | undefined>(undefined);

    readonly hotelType = signal<BabylonBodyExtra | undefined>(undefined);

    readonly hotelCategory = signal<BabylonBodyExtra | undefined>(undefined);

    readonly ticker = signal<BabylonBodyExtra | undefined>(undefined);

    readonly firstPageLoaded = signal<boolean>(false);

    get headerDataInstant():
        | BabylonHeaderBookShowI
        | BabylonHeaderClearI
        | BabylonHeaderMenuCenterI
        | BabylonHeaderMenuShowI
        | BabylonHeaderMenuLogoCenterI
        | undefined {
        return this.headerData();
    }

    get engineDataInstant(): BabylonEngineI | undefined {
        return this.engineData();
    }

    get headInstant(): PageConfigHead | undefined {
        return this.pageHead();
    }

    get footerLinksDataInstant():
        | BodyComponent<BabylonFooterLinksI>
        | undefined {
        return this.footerLinks();
    }

    get foc3imgtxtDataInstant(): BodyComponent<any> | undefined {
        return this.foc3imgtxt();
    }

    get footerLinksSvgDataInstant():
        | BodyComponent<BabylonFooterLinksI>
        | undefined {
        return this.footerLinksSvg();
    }

    get footerLogosDataInstant():
        | BodyComponent<BabylonFooterLogosI>
        | undefined {
        return this.footerLogos();
    }

    get footerContactDataInstant():
        | BodyComponent<BabylonFooterContactI>
        | undefined {
        return this.footerContact();
    }

    get folislinksDataInstant():
        | BodyComponent<BabylonFooterLinksI>
        | undefined {
        return this.folislinks();
    }

    get foC3TxtIcoV2DataInstant():
        | BodyComponent<BabylonFooterLinksI>
        | undefined {
        return this.foC3TxtIcoV2();
    }

    get offersListDataInstant(): BodyComponent<BabylonOffersListI> | undefined {
        return this.offersList();
    }

    get dynamicHeaderInstant(): boolean {
        return this.dynamicHeader();
    }

    get loadFooterInstant(): boolean {
        return this.loadFooter();
    }

    get loadHeadersInstant(): boolean {
        return this.loadHeaders();
    }

    get advantagesUpInstant(): boolean {
        return this.advantagesUp();
    }

    get pageTypeInstant(): boolean {
        return this.pageType();
    }

    get page_TypeInstant(): string | null {
        return this._pageType();
    }

    get selectedHeaderInstant(): BabylonHeadersType {
        return this.selectedHeader();
    }

    get engineAlreadyExistsInstant(): boolean {
        return this.engineAlreadyExists();
    }

    get footerInstant():
        | BodyComponent<BabylonStaticFooterI | BabylonStaticFooterV2I>
        | undefined {
        return this.footer();
    }

    get foC0SvgLinkInstant():
        | BodyComponent<BabylonStaticFooterI | BabylonStaticFooterV2I>
        | undefined {
        return this.foC0SvgLink();
    }

    setLayoutElements<T>(
        response?: PageConfig<T | undefined>,
        isHome?: boolean,
        isHeaderDynamic: boolean = false
    ): void {
        if (response) {
            this.setHead(response.head);
            this.dynamicHeader.set(!!isHome || (isHeaderDynamic ?? true));

            const topSliderComp = Utils.findComponent(
                response.body,
                'topslider'
            );
            const topSliderVideoComp = Utils.findComponent(
                response.body,
                'topslidervideo'
            );
            const topSliderVideoV2Comp = Utils.findComponent(
                response.body,
                'topslidervideov2'
            );

            this.topSlider.set(
                topSliderComp
                    ? {
                          name: 'topslider',
                          props: topSliderComp as BabylonTopSliderI,
                      }
                    : topSliderVideoComp
                      ? {
                            name: 'topslidervideo',
                            props: topSliderVideoComp as BabylonTopSliderVideoI,
                        }
                      : topSliderVideoV2Comp
                        ? {
                              name: 'topslidervideov2',
                              props: topSliderVideoV2Comp as any,
                          }
                        : undefined
            );

            this.breadcrumbs.set(
                response.bodyExtra
                    ? (Utils.findComponent<COMPONENTS_EXTRA>(
                          response.bodyExtra,
                          'breadcrumbs'
                      ) as BabylonBreadcrumbI)
                    : undefined
            );

            this.breadcrumbsv2.set(
                response.bodyExtra
                    ? (Utils.findComponent<COMPONENTS_EXTRA>(
                          response.bodyExtra,
                          'breadcrumbsV2'
                      ) as BabylonBreadcrumbI)
                    : undefined
            );

            this.advantagesUp.set(
                Utils.findComponent(response.body, 'advantages') !== undefined
            );
            this.pageDataLoaded.set(true);
            this.loadFooter.set(true);
            this.loadHeaders.set(true);

            this.submenu.set(
                response.bodyExtra
                    ? (Utils.findComponent<COMPONENTS_EXTRA>(
                          response.bodyExtra,
                          'menu'
                      ) as BabylonSubmenuI)
                    : undefined
            );

            this.hotelType.set(
                response.bodyExtra
                    ? (Utils.findComponent<COMPONENTS_EXTRA>(
                          response.bodyExtra,
                          'hoteltype'
                      ) as BabylonBodyExtra)
                    : undefined
            );

            this.hotelCategory.set(
                response.bodyExtra
                    ? (Utils.findComponent<COMPONENTS_EXTRA>(
                          response.bodyExtra,
                          'hotelcategory'
                      ) as BabylonBodyExtra)
                    : undefined
            );

            this.ticker.set(
                response.bodyExtra
                    ? (Utils.findComponent<COMPONENTS_EXTRA>(
                          response.bodyExtra,
                          'ticker'
                      ) as BabylonBodyExtra)
                    : undefined
            );

            const roomsModalComp = (response.body ?? [])
                .filter((component: any) => component.name === 'modalroomslist')
                .map((component: any) => ({
                    ...component,
                    name: component.alias?.toLowerCase(),
                }));
            this.roomsModal.set(roomsModalComp);

            const globalModals = (response.body ?? [])
                .filter((component: any) => component.name === 'globalmodal')
                .map((component: any) => ({
                    ...component,
                    name: component.alias?.toLowerCase(),
                }));
            this.globalModal.set(globalModals);

            const modalPopups = (response.body ?? [])
                .filter((component: any) => component.name === 'modalpopup')
                .map((component: any) => ({
                    ...component,
                    name: component.alias?.toLowerCase(),
                }));
            this.modalPopup.set(modalPopups);

            const offersList = Utils.findComponent(
                response.body,
                'offerslist'
            ) as BabylonOffersListI | undefined;
            const safeOffersList = offersList?.items
                ? offersList
                : { ...offersList, items: [] };
            const filteredItems = isHome
                ? safeOffersList.items?.filter(
                      (offer: any) => offer.home === 1 || offer.home === '1'
                  )
                : safeOffersList.items;
            const filteredOffersList: BodyComponent<BabylonOffersListI> = {
                name: 'offerslist',
                props: {
                    ...safeOffersList,
                    items: filteredItems,
                },
            };
            this.offersList.set(filteredOffersList);
        }
    }

    setheaderData(
        headerData:
            | BabylonHeaderBookShowI
            | BabylonHeaderClearI
            | BabylonHeaderMenuCenterI
            | BabylonHeaderMenuShowI
            | BabylonHeaderMenuLogoCenterI
            | undefined
    ): void {
        this.headerData.set(headerData);
    }

    setPageType(page: boolean | undefined): void {
        this.pageType.set(page ?? false);
    }

    set_PageType(type: string | null): void {
        this._pageType.set(type);
    }

    setHead(head: PageConfigHead | undefined): void {
        this.pageHead.set(head);
    }

    setLoadFooter(loadFooter: boolean): void {
        this.loadFooter.set(loadFooter);
    }

    setLoadHeaders(loadHeaders: boolean): void {
        this.loadHeaders.set(loadHeaders);
    }

    setEngineAlreadyExists(engineAlreadyExists: boolean): void {
        this.engineAlreadyExists.set(engineAlreadyExists);
    }

    setFoC0SvgLinkData(
        foC0svglink:
            | BodyComponent<BabylonStaticFooterI | BabylonStaticFooterV2I>
            | undefined
    ): void {
        this.foC0SvgLink.set(foC0svglink);
    }

    setFooter(
        footer:
            | BodyComponent<BabylonStaticFooterI | BabylonStaticFooterV2I>
            | undefined
    ): void {
        this.footer.set(footer);
    }

    setEngineData(engineData: BabylonEngineI | undefined): void {
        this.engineData.set(engineData);
    }

    setFoC3TxtIcoV2Data(
        foC3TxtIcoV2: BodyComponent<BabylonFooterLinksI> | undefined
    ): void {
        this.foC3TxtIcoV2.set(foC3TxtIcoV2);
    }

    setSelectedHeader(selectedHeader: BabylonHeadersType): void {
        this.selectedHeader.set(selectedHeader);
    }

    setFooterLogosData(
        footerlogos: BodyComponent<BabylonFooterLogosI> | undefined
    ): void {
        this.footerLogos.set(footerlogos);
    }

    setFooterLinksData(
        footerlinks: BodyComponent<BabylonFooterLinksI> | undefined
    ): void {
        this.footerLinks.set(footerlinks);
    }

    setFoC3ImgTxtData(footerlinks: BodyComponent<any> | undefined): void {
        this.foc3imgtxt.set(footerlinks);
    }

    setFolislinksData(
        folislinks: BodyComponent<BabylonFooterLinksI> | undefined
    ): void {
        this.folislinks.set(folislinks);
    }

    setFooterContactData(
        footerContact: BodyComponent<BabylonFooterContactI> | undefined
    ): void {
        this.footerContact.set(footerContact);
    }

    setOffersListData(
        offersList: BodyComponent<BabylonOffersListI> | undefined
    ): void {
        this.offersList.set(offersList);
    }

    openGlobalModal(modalSvc: BabylonModalService, alias?: string): void {
        //for test
        if (alias === 'offerPopUpV3') {
            modalSvc.openOfferModal();
        }
        const globalModal = this.globalModal();
        if (globalModal) {
            if (alias) {
                for (let modal of globalModal) {
                    if (modal.alias?.toLowerCase() === alias.toLowerCase()) {
                        modalSvc.openReserveModal(modal.props);
                        return;
                    }
                }
            }
        }
    }

    openModalPopup(modalSvc: BabylonModalService, alias?: string): void {
        const modalPopup = this.modalPopup();
        if (modalPopup) {
            if (alias) {
                for (let modal of modalPopup) {
                    if (modal.alias?.toLowerCase() === alias.toLowerCase()) {
                        modalSvc.openPopupModal(modal.props);
                        return;
                    }
                }
            }
        }
    }
}
